import { PermissionEnum } from '@Application/Security/Roles';
import FetchDocumentTemplateVersions from '@Application/Documents/Templates/Queries/FetchDocumentTemplateVersions';
import FetchDocumentTemplates from '@Application/Documents/Templates/Queries/FetchDocumentTemplates';
import fetchDocumentTemplateVersions from '@Application/Documents/Templates/fetchDocumentTemplateVersions';
import fetchDocumentTemplates from '@Application/Documents/Templates/fetchDocumentTemplates';
import FetchFacilityTemplateOverrides from '@Application/Documents/TemplateAssignments/Queries/FetchFacilityTemplateOverrides';
import FetchTemplateAssignments from '@Application/Documents/TemplateAssignments/Queries/FetchTemplateAssignments';
import type { TemplateAssignmentScope } from '@Application/Documents/TemplateAssignments/TemplateAssignmentScope';
import fetchFacilityTemplateOverrides from '@Application/Documents/TemplateAssignments/fetchFacilityTemplateOverrides';
import fetchTemplateAssignments from '@Application/Documents/TemplateAssignments/fetchTemplateAssignments';
import FetchFacilities from '@Application/Facilities/Facilities/Queries/FetchFacilities';
import fetchFacilities from '@Application/Facilities/Facilities/fetchFacilities';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import PillTabView from '@Guidelines/Component/PillTabView';
import DocumentTemplatesSection from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/DocumentTemplates';
import TemplateAssignmentsSection from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/TemplateAssignements';
import useDocumentTemplatesTab from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Hooks/useDocumentTemplatesTab';
import { openDocumentTemplateUploadSidebar } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateUploadSidebarSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { css } from '@emotion/react';
import { FileText, Layers, Plus, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, TabPanel } from '@verclam/ui-kit';
import type { TabViewTabChangeEvent } from '@verclam/ui-kit';
import DocumentTemplatesSkeleton from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/DocumentTemplatesSkeleton';

const LOAD_ERROR_TITLE = 'Impossible de charger les gabarits.';

/**
 * The scrolling ancestor the tab bar sticks against: `PillTabView isSticky` pins itself to the top of
 * the nearest scroll port, and poses no overflow of its own. `flex: 1` with `min-height: 0` is what
 * bounds this box below the page header, without which it would grow instead of scrolling — and the
 * sticky, having nothing to scroll inside, would never take effect.
 */
const containerStyle = css`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 0 1rem;
`;

/**
 * Acts on the current organization — the header selector's own choice, never a list to enter. Owns
 * every query the screen needs: the gabarit catalogue, its versions, the organization's own
 * affectations and the facility overrides across the organization. A fifth query, `facilitiesStore`,
 * resolves facility names for the overrides list — its own failure never enters this screen's
 * `ErrorState`: a missing name degrades to a "Agence n° {id}" fallback instead, read directly by
 * `TemplateAssignmentOverridesList`. The active tab itself is controlled from the `tab` query
 * parameter (`useDocumentTemplatesTab`), shared with the page header's action button, so a reload or
 * a shared link always lands back on the same tab.
 */
const OrganizationDocumentTemplatesContent: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const { activeIndex, setActiveIndex } = useDocumentTemplatesTab();

	const {
		documentTypes,
		error: documentTypesError,
		refetch: refetchDocumentTypes,
	} = useAccountDocumentTypes();

	const scope = React.useMemo<TemplateAssignmentScope | null>(() => {
		if (organizationId === null) {
			return null;
		}

		return {
			type: 'organization',
			id: organizationId,
		};
	}, [organizationId]);

	const {
		data: templates,
		error: templatesError,
		refetch: refetchTemplates,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.templates,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchDocumentTemplates(new FetchDocumentTemplates(organizationId)));
		},
		deps: [organizationId],
	});

	const {
		data: templateVersions,
		error: templateVersionsError,
		refetch: refetchTemplateVersions,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.templateVersions,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchDocumentTemplateVersions(new FetchDocumentTemplateVersions(organizationId)));
		},
		deps: [organizationId],
	});

	const {
		data: organizationAssignments,
		error: assignmentsError,
		refetch: refetchAssignments,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.organizationAssignments,
		stopOn: (value) =>
			value !== null && value.scopeType === 'organization' && value.scopeId === organizationId,
		query: () => {
			if (scope === null) {
				return Promise.resolve();
			}

			return dispatch(fetchTemplateAssignments(new FetchTemplateAssignments(scope)));
		},
		deps: [organizationId],
	});

	const {
		data: facilityOverrides,
		error: overridesError,
		refetch: refetchOverrides,
	} = useStoreQuery({
		selector: (state) => state.documentTemplatesStore.facilityOverrides,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchFacilityTemplateOverrides(new FetchFacilityTemplateOverrides(organizationId)),
			);
		},
		deps: [organizationId],
	});

	// Its own `error` is deliberately never read: a failure here degrades to a fallback label,
	// never the screen's `ErrorState`.
	const { data: facilities } = useStoreQuery({
		selector: (state) => state.facilitiesStore.facilities,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchFacilities(new FetchFacilities(organizationId)));
		},
		deps: [organizationId],
	});

	const handleRetry = React.useCallback(() => {
		refetchTemplates();
		refetchTemplateVersions();
		refetchAssignments();
		refetchOverrides();
		refetchDocumentTypes();
	}, [
		refetchTemplates,
		refetchTemplateVersions,
		refetchAssignments,
		refetchOverrides,
		refetchDocumentTypes,
	]);

	const handleUploadClick = React.useCallback(() => {
		openDocumentTemplateUploadSidebar({ mode: 'create' });
	}, []);

	const handleTabChange = React.useCallback(
		(event: TabViewTabChangeEvent) => {
			setActiveIndex(event.index);
		},
		[setActiveIndex],
	);

	if (organizationId === null) {
		return <EmptyState title="Aucune structure n’est sélectionnée." icon={<TriangleAlert size={28} />} />;
	}

	if (templatesError || templateVersionsError || assignmentsError || overridesError || documentTypesError) {
		return <ErrorState title={LOAD_ERROR_TITLE} onRetry={handleRetry} />;
	}

	if (
		organizationAssignments === null ||
		facilityOverrides === null ||
		templates === null ||
		templateVersions === null ||
		documentTypes === null
	) {
		return <DocumentTemplatesSkeleton />;
	}

	if (templates.list.length === 0) {
		return (
			<EmptyState
				title="Aucun gabarit n’a encore été déposé."
				description="Tant qu’aucun ne l’est, aucun document ne pourra être téléchargé."
				action={
					<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}>
						<Button
							type="button"
							label="Déposer un gabarit"
							icon={<Plus size={16} />}
							onClick={handleUploadClick}
						/>
					</WithPermission>
				}
			/>
		);
	}

	return (
		<div css={containerStyle}>
			<PillTabView isSticky activeIndex={activeIndex} onTabChange={handleTabChange}>
				<TabPanel header="Affectation par type" leftIcon={<Layers size={18} />}>
					<TemplateAssignmentsSection
						organizationId={organizationId}
						assignments={organizationAssignments}
						templates={templates}
						documentTypes={documentTypes}
						facilityOverrides={facilityOverrides}
						facilities={facilities}
					/>
				</TabPanel>
				<TabPanel header={`Gabarits (${templates.list.length})`} leftIcon={<FileText size={18} />}>
					<DocumentTemplatesSection
						organizationId={organizationId}
						templates={templates}
						templateVersions={templateVersions}
						organizationAssignments={organizationAssignments}
						facilityOverrides={facilityOverrides}
						documentTypes={documentTypes}
					/>
				</TabPanel>
			</PillTabView>
		</div>
	);
};

export default OrganizationDocumentTemplatesContent;
